import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Truck, Star, ChevronRight, ShoppingCart, Calendar, User, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { SiteNav } from '@/components/SiteNav';
import { SiteFooter } from '@/components/SiteFooter';
import { getProducts, getPosts } from '@/lib/store';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/hooks/useToast';

// Tribal / petroglyphic SVG icons for each category
const TribalHunting = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Deer / buck — antlers + body */}
    <path d="M24 30 L24 20" />
    <path d="M24 20 L18 12 L16 8 M24 20 L30 12 L32 8" />
    <path d="M18 12 L14 10 M30 12 L34 10" />
    <ellipse cx="24" cy="33" rx="6" ry="4" />
    <line x1="20" y1="37" x2="19" y2="42" />
    <line x1="28" y1="37" x2="29" y2="42" />
    <circle cx="24" cy="19" r="2" />
  </svg>
);

const TribalFishing = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Fish — angular/tribal */}
    <path d="M8 24 L36 24" />
    <path d="M36 24 L44 18 L44 30 Z" />
    <path d="M8 24 L14 17 L20 24 L14 31 Z" />
    <circle cx="27" cy="24" r="1.5" fill="currentColor" />
    <path d="M22 20 L22 28 M28 19 L28 29" strokeWidth="1.5" />
  </svg>
);

const TribalArchery = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Arrow with feathers */}
    <line x1="6" y1="42" x2="40" y2="8" />
    <path d="M40 8 L34 10 L38 14 Z" fill="currentColor" />
    <path d="M6 42 L10 36 M6 42 L12 38" strokeWidth="2" />
    {/* Bow arc */}
    <path d="M14 38 Q4 24 14 10" strokeWidth="2" fill="none" />
    <line x1="14" y1="38" x2="14" y2="10" strokeWidth="1.5" strokeDasharray="3 2" />
  </svg>
);

const TribalApparel = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Tribal tunic / vest shape */}
    <path d="M16 6 L8 14 L12 16 L12 40 L36 40 L36 16 L40 14 L32 6 Z" />
    <path d="M16 6 L20 10 L24 6 L28 10 L32 6" />
    {/* Decorative tribal chevrons */}
    <path d="M18 22 L24 18 L30 22" strokeWidth="1.5" />
    <path d="M18 28 L24 24 L30 28" strokeWidth="1.5" />
  </svg>
);

const TribalCamping = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Tribal tent / tipi */}
    <path d="M24 4 L6 40 L42 40 Z" />
    <path d="M24 4 L20 40 M24 4 L28 40" strokeWidth="1.2" />
    {/* Door */}
    <path d="M20 40 Q24 32 28 40" />
    {/* Stars / sky marks */}
    <circle cx="10" cy="14" r="1.2" fill="currentColor" />
    <circle cx="38" cy="10" r="1.2" fill="currentColor" />
    <path d="M36 18 L38 14 L40 18 M36 18 L40 18" strokeWidth="1.2" />
  </svg>
);

const TribalAccessories = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Eye / trail camera — tribal eye motif */}
    <path d="M6 24 Q16 10 24 10 Q32 10 42 24 Q32 38 24 38 Q16 38 6 24 Z" />
    <circle cx="24" cy="24" r="6" />
    <circle cx="24" cy="24" r="2.5" fill="currentColor" />
    {/* Tribal lashes */}
    <line x1="24" y1="10" x2="24" y2="6" />
    <line x1="24" y1="38" x2="24" y2="42" />
    <line x1="8" y1="20" x2="5" y2="18" />
    <line x1="40" y1="20" x2="43" y2="18" />
  </svg>
);

const categories = [
  { title: 'Hunting', href: '/hunting', description: 'Rifles, optics, blinds, calls & decoys', Icon: TribalHunting, bg: 'from-stone-800 to-stone-900', accent: 'bg-amber-600', color: 'text-amber-300' },
  { title: 'Fishing', href: '/fishing', description: 'Rods, reels, tackle & ice fishing', Icon: TribalFishing, bg: 'from-slate-700 to-slate-900', accent: 'bg-blue-600', color: 'text-blue-300' },
  { title: 'Archery', href: '/archery', description: 'Compound bows, crossbows & accessories', Icon: TribalArchery, bg: 'from-green-900 to-stone-900', accent: 'bg-green-600', color: 'text-green-300' },
  { title: 'Apparel', href: '/apparel', description: 'Camo, base layers, boots & hats', Icon: TribalApparel, bg: 'from-amber-900 to-stone-900', accent: 'bg-amber-700', color: 'text-amber-200' },
  { title: 'Camping', href: '/camping', description: 'Tents, knives, survival & fire starters', Icon: TribalCamping, bg: 'from-red-900 to-stone-900', accent: 'bg-red-700', color: 'text-red-300' },
  { title: 'Accessories', href: '/accessories', description: 'Trail cameras, packs, calls & more', Icon: TribalAccessories, bg: 'from-zinc-700 to-zinc-900', accent: 'bg-zinc-500', color: 'text-zinc-300' },
];

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const Index = () => {
  useSeoMeta({
    title: 'Dakota Horizon Outdoors — Premium Hunting & Outdoor Gear',
    description: 'Shop premium hunting, fishing, archery, and outdoor gear. Dakota Horizon Outdoors — built for the wild.',
    ogTitle: 'Dakota Horizon Outdoors',
    ogDescription: 'Premium hunting, fishing, and outdoor gear for the serious sportsman.',
    ogImage: 'https://blossom.ditto.pub/ce692ec76cbc6a273a3faa10638d7b4447b636afc247dc81c369153071f8b9d8.jpeg',
  });

  const { addToCart } = useCart();
  const { toast } = useToast();

  const allProducts = getProducts();
  const featuredProducts = allProducts.filter((p) => p.featured).slice(0, 4);
  const recentPosts = getPosts().filter((p) => p.published).sort((a, b) => b.publishedAt - a.publishedAt).slice(0, 3);

  function handleAddToCart(productId: string, name: string) {
    addToCart(productId);
    toast({ title: `${name} added to cart!` });
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteNav />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(15,25,8,0.88) 0%, rgba(40,25,10,0.80) 100%), 
              url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80')`,
          }}
        />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px',
        }} />

        <div className="relative container mx-auto px-4 pt-24 pb-16 text-center">
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-amber-500/20 blur-xl scale-150" />
              <img src="/images/DHO.jpg" alt="Dakota Horizon Outdoors" className="relative h-28 w-28 md:h-36 md:w-36 rounded-full object-cover ring-4 ring-amber-600/70 shadow-2xl" />
            </div>
          </div>

          <div className="inline-flex items-center gap-2 bg-amber-600/20 border border-amber-600/40 rounded-full px-4 py-1.5 mb-6">
            <span className="text-amber-400 text-xs font-medium tracking-widest uppercase">Est. in the Wild</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4 leading-tight">
            DAKOTA HORIZON
            <span className="block text-amber-500">OUTDOORS</span>
          </h1>

          <p className="text-lg md:text-2xl text-stone-300 max-w-2xl mx-auto mb-10 leading-relaxed font-light">
            Premium hunting, fishing, and outdoor gear — engineered for those who refuse to settle for anything less than the wild.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-8 py-6 text-base rounded-sm shadow-lg">
              <Link to="/hunting">Shop Now <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="bg-white/15 border-white/60 text-white hover:bg-transparent hover:border-white/30 hover:text-white/70 px-8 py-6 text-base rounded-sm transition-colors">
              <Link to="/about">Our Story</Link>
            </Button>
          </div>

          <div className="mt-16 flex justify-center motion-safe:animate-bounce">
            <ChevronRight className="h-6 w-6 text-amber-500/60 rotate-90" />
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-amber-700 py-3 sm:py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-white text-xs sm:text-sm font-medium">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Truck className="h-4 w-4" />
              <span>Free Shipping on Orders $75+</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/30" />
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Shield className="h-4 w-4" />
              <span>Lifetime Warranty on Select Gear</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/30" />
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Star className="h-4 w-4" />
              <span>Trusted by 50,000+ Sportsmen</span>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 sm:py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 sm:mb-12">
            <Badge className="bg-amber-700/20 text-amber-700 border-amber-700/30 mb-3">Shop by Category</Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">Gear Up for the Hunt</h2>
            <p className="text-muted-foreground mt-3 text-base sm:text-lg max-w-xl mx-auto">
              Everything you need for the field, the water, and the wild.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {categories.map((cat) => (
              <Link key={cat.href} to={cat.href} className={`relative overflow-hidden rounded-lg bg-gradient-to-br ${cat.bg} p-4 sm:p-6 md:p-8 product-card-hover group`}>
                <div className={`absolute top-3 right-3 h-2 w-2 rounded-full ${cat.accent}`} />
                <div className={`mb-3 sm:mb-4 ${cat.color}`}><cat.Icon /></div>
                <h3 className="text-white font-bold text-base sm:text-lg md:text-xl mb-0.5 sm:mb-1">{cat.title}</h3>
                <p className="text-stone-400 text-xs sm:text-sm hidden sm:block">{cat.description}</p>
                <div className="mt-3 sm:mt-4 flex items-center text-amber-400 text-xs sm:text-sm font-medium">
                  <span>Shop</span>
                  <ArrowRight className="ml-1 h-3.5 w-3.5 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-16 sm:py-20 bg-muted/40">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between mb-8 sm:mb-10">
              <div>
                <Badge className="bg-amber-700/20 text-amber-700 border-amber-700/30 mb-2 sm:mb-3">Featured</Badge>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">Top Picks This Season</h2>
              </div>
              <Link to="/hunting" className="hidden sm:flex items-center gap-1 text-amber-700 hover:text-amber-600 font-medium text-sm transition-colors">
                View All <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {featuredProducts.map((product) => (
                <Card key={product.id} className="overflow-hidden product-card-hover border-border/50 bg-card group">
                  <div className="aspect-square bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-800 dark:to-stone-900 relative flex items-center justify-center overflow-hidden">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <ImageIcon className="h-10 w-10 text-stone-300 dark:text-stone-600" />
                    )}
                    {product.badge && (
                      <span className="absolute top-2 left-2 bg-amber-600 text-white text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-sm">
                        {product.badge}
                      </span>
                    )}
                  </div>
                  <CardContent className="p-3 sm:p-4">
                    <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1 capitalize">{product.category}</p>
                    <h3 className="font-semibold text-foreground text-xs sm:text-sm leading-snug mb-2 sm:mb-3 line-clamp-2">{product.name}</h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-bold text-foreground text-sm sm:text-base">${product.price.toFixed(2)}</span>
                        {product.originalPrice && (
                          <span className="text-muted-foreground text-xs line-through ml-1">${product.originalPrice.toFixed(2)}</span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAddToCart(product.id, product.name)}
                        className="bg-amber-700 hover:bg-amber-600 text-white text-xs rounded-sm h-7 sm:h-8 px-2 sm:px-3"
                      >
                        <ShoppingCart className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">Add</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Brand Story Banner */}
      <section className="relative py-20 sm:py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10,20,5,0.92) 40%, rgba(10,20,5,0.6) 100%), 
              url('https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=1400&q=80')`,
          }}
        />
        <div className="relative container mx-auto px-4">
          <div className="max-w-xl">
            <Badge className="bg-amber-600/30 text-amber-400 border-amber-600/50 mb-4">Our Mission</Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Forged from the Prairie, Built for the Hunt
            </h2>
            <p className="text-stone-300 text-base sm:text-lg leading-relaxed mb-8">
              Dakota Horizon Outdoors was born from years in the field — from early morning duck blinds to late-season whitetail stands. We build gear we'd trust with our own hunts.
            </p>
            <Button asChild className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-6 py-5 rounded-sm">
              <Link to="/about">Read Our Story <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Blog Preview (replaces Testimonials) */}
      {recentPosts.length > 0 && (
        <section className="py-16 sm:py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between mb-8 sm:mb-10">
              <div>
                <Badge className="bg-amber-700/20 text-amber-700 border-amber-700/30 mb-2 sm:mb-3">Field Notes</Badge>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">From the Field Blog</h2>
              </div>
              <Link to="/blog" className="hidden sm:flex items-center gap-1 text-amber-700 hover:text-amber-600 font-medium text-sm transition-colors">
                All Posts <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {recentPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group block">
                  <Card className="overflow-hidden border-border/50 product-card-hover h-full">
                    <div className="aspect-video bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-800 dark:to-stone-900 overflow-hidden">
                      {post.imageUrl ? (
                        <img src={post.imageUrl} alt={post.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          <img src="/images/DHO.jpg" alt="DHO" className="h-14 w-14 object-contain opacity-20" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4 sm:p-5">
                      <h3 className="font-bold text-foreground text-sm sm:text-base leading-snug group-hover:text-amber-700 transition-colors line-clamp-2 mb-2">{post.title}</h3>
                      <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed line-clamp-2 mb-3">{post.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><User className="h-3 w-3" />{post.author}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(post.publishedAt)}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            <div className="mt-6 text-center sm:hidden">
              <Button asChild variant="outline" className="rounded-sm">
                <Link to="/blog">Read All Posts <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="py-16 sm:py-20 bg-[hsl(85,35%,18%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">Stay on the Trail</h2>
          <p className="text-stone-300 mb-8 max-w-md mx-auto text-sm sm:text-base">
            Get early access to new gear drops, seasonal sales, and field reports straight to your inbox.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-sm bg-white/10 border border-white/20 text-white placeholder-stone-400 focus:outline-none focus:border-amber-500 focus:bg-white/15 transition-colors text-sm"
            />
            <Button className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-6 py-3 rounded-sm whitespace-nowrap text-sm">
              Sign Me Up
            </Button>
          </form>
          <p className="text-stone-500 text-xs mt-3">No spam, ever. Unsubscribe at any time.</p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Index;
